import { useState, useCallback, useRef } from "react";

// Timeout for guide generation (20 minutes)
// Matches backend queue timing more closely and reduces false timeout UI states.
export const GENERATION_TIMEOUT_MS = 20 * 60 * 1000;

// localStorage key for persisting pending guides across page refreshes
const STORAGE_KEY = "pending-automation-guides";
const DIRECT_STORAGE_KEY = "pending-direct-automation-guides";

export interface PendingGuide {
  sopId: string;
  sopTitle: string;
  startedAt: number;
  jobId?: string;
}

// For direct guides (no SOP), we track by jobId instead
export interface PendingDirectGuide {
  jobId: string;
  processName: string;
  startedAt: number;
}

/**
 * Result of a localStorage operation
 * When success is false, the error type indicates what went wrong
 */
export interface StorageResult {
  success: boolean;
  errorType?: "quota_exceeded" | "security_error" | "unknown";
  errorMessage?: string;
}

/**
 * Categorize localStorage errors for better error handling
 */
function categorizeStorageError(e: unknown): StorageResult {
  if (e instanceof DOMException) {
    // QuotaExceededError - storage is full (common on mobile)
    if (e.name === "QuotaExceededError" || e.code === 22) {
      console.error(
        "[localStorage] Quota exceeded - storage is full:",
        e.message,
      );
      return {
        success: false,
        errorType: "quota_exceeded",
        errorMessage:
          "Storage is full. Guide progress tracking may not persist across sessions.",
      };
    }
    // SecurityError - localStorage is blocked (private browsing, etc.)
    if (e.name === "SecurityError") {
      console.error(
        "[localStorage] Security error - storage blocked:",
        e.message,
      );
      return {
        success: false,
        errorType: "security_error",
        errorMessage:
          "Storage access denied. Guide progress will not persist across sessions.",
      };
    }
  }

  console.error("[localStorage] Unknown error:", e);
  return {
    success: false,
    errorType: "unknown",
    errorMessage: "Failed to save progress. Guide tracking may not persist.",
  };
}

// Helper to save pending guides to localStorage
function savePendingGuides(guides: Map<string, PendingGuide>): StorageResult {
  try {
    const data = Array.from(guides.values());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return { success: true };
  } catch (e) {
    return categorizeStorageError(e);
  }
}

// Helper to load pending guides from localStorage
function loadPendingGuides(): Map<string, PendingGuide> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Map();

    const data = JSON.parse(stored) as PendingGuide[];
    const map = new Map<string, PendingGuide>();
    const now = Date.now();

    // Filter out expired jobs (older than GENERATION_TIMEOUT_MS)
    for (const guide of data) {
      if (now - guide.startedAt < GENERATION_TIMEOUT_MS) {
        map.set(guide.sopId, guide);
      }
    }

    // If any were filtered out, update storage
    if (map.size !== data.length) {
      savePendingGuides(map);
    }

    return map;
  } catch (e) {
    console.error("Failed to load pending guides from localStorage:", e);
    return new Map();
  }
}

// Helper to remove a pending guide from localStorage
export function removePendingGuide(sopId: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    const data = JSON.parse(stored) as PendingGuide[];
    const filtered = data.filter((g) => g.sopId !== sopId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.error("Failed to remove pending guide from localStorage:", e);
  }
}

// Helper to save pending direct guides to localStorage
function savePendingDirectGuides(
  guides: Map<string, PendingDirectGuide>,
): StorageResult {
  try {
    const data = Array.from(guides.values());
    localStorage.setItem(DIRECT_STORAGE_KEY, JSON.stringify(data));
    return { success: true };
  } catch (e) {
    return categorizeStorageError(e);
  }
}

// Helper to load pending direct guides from localStorage
function loadPendingDirectGuides(): Map<string, PendingDirectGuide> {
  try {
    const stored = localStorage.getItem(DIRECT_STORAGE_KEY);
    if (!stored) return new Map();

    const data = JSON.parse(stored) as PendingDirectGuide[];
    const map = new Map<string, PendingDirectGuide>();
    const now = Date.now();

    // Filter out expired jobs (older than GENERATION_TIMEOUT_MS)
    for (const guide of data) {
      if (now - guide.startedAt < GENERATION_TIMEOUT_MS) {
        map.set(guide.jobId, guide);
      }
    }

    // If any were filtered out, update storage
    if (map.size !== data.length) {
      savePendingDirectGuides(map);
    }

    return map;
  } catch (e) {
    console.error("Failed to load pending direct guides from localStorage:", e);
    return new Map();
  }
}

// Helper to remove a pending direct guide from localStorage
export function removePendingDirectGuide(jobId: string): void {
  try {
    const stored = localStorage.getItem(DIRECT_STORAGE_KEY);
    if (!stored) return;

    const data = JSON.parse(stored) as PendingDirectGuide[];
    const filtered = data.filter((g) => g.jobId !== jobId);
    localStorage.setItem(DIRECT_STORAGE_KEY, JSON.stringify(filtered));
  } catch (e) {
    console.error(
      "Failed to remove pending direct guide from localStorage:",
      e,
    );
  }
}

export function usePendingGuidesPersistence() {
  // Track SOPs with pending guide generation: sopId -> { sopTitle, startedAt }
  // Initialize from localStorage to persist across page refreshes
  const [pendingGuides, setPendingGuides] = useState<Map<string, PendingGuide>>(
    () => loadPendingGuides(),
  );
  // Track direct guides (no SOP): jobId -> { processName, startedAt }
  const [pendingDirectGuides, setPendingDirectGuides] = useState<
    Map<string, PendingDirectGuide>
  >(() => loadPendingDirectGuides());

  // Track the last storage error so consumers can display it
  const [lastStorageError, setLastStorageError] =
    useState<StorageResult | null>(null);

  // Track if we've already shown the storage error warning (avoid spamming)
  const hasShownStorageWarningRef = useRef(false);

  // Clear storage error (call after displaying notification)
  const clearStorageError = useCallback(() => {
    setLastStorageError(null);
  }, []);

  // Add a pending guide
  const addPendingGuide = useCallback((guide: PendingGuide): StorageResult => {
    let result: StorageResult = { success: true };
    setPendingGuides((prev) => {
      const next = new Map(prev);
      next.set(guide.sopId, guide);
      result = savePendingGuides(next);
      if (!result.success && !hasShownStorageWarningRef.current) {
        setLastStorageError(result);
        hasShownStorageWarningRef.current = true;
      }
      return next;
    });
    return result;
  }, []);

  // Remove a pending guide
  const deletePendingGuide = useCallback((sopId: string) => {
    setPendingGuides((prev) => {
      if (!prev.has(sopId)) return prev;
      const next = new Map(prev);
      next.delete(sopId);
      removePendingGuide(sopId);
      return next;
    });
  }, []);

  // Update job ID for a pending guide
  const updatePendingGuideJobId = useCallback(
    (sopId: string, jobId: string): StorageResult => {
      let result: StorageResult = { success: true };
      setPendingGuides((prev) => {
        const existing = prev.get(sopId);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(sopId, { ...existing, jobId });
        result = savePendingGuides(next);
        if (!result.success && !hasShownStorageWarningRef.current) {
          setLastStorageError(result);
          hasShownStorageWarningRef.current = true;
        }
        return next;
      });
      return result;
    },
    [],
  );

  // Add a pending direct guide
  const addPendingDirectGuide = useCallback(
    (guide: PendingDirectGuide): StorageResult => {
      let result: StorageResult = { success: true };
      setPendingDirectGuides((prev) => {
        const next = new Map(prev);
        next.set(guide.jobId, guide);
        result = savePendingDirectGuides(next);
        if (!result.success && !hasShownStorageWarningRef.current) {
          setLastStorageError(result);
          hasShownStorageWarningRef.current = true;
        }
        return next;
      });
      return result;
    },
    [],
  );

  // Remove a pending direct guide
  const deletePendingDirectGuide = useCallback((jobId: string) => {
    setPendingDirectGuides((prev) => {
      if (!prev.has(jobId)) return prev;
      const next = new Map(prev);
      next.delete(jobId);
      removePendingDirectGuide(jobId);
      return next;
    });
  }, []);

  // Check if generating
  const isGenerating = useCallback(
    (sopId: string) => pendingGuides.has(sopId),
    [pendingGuides],
  );

  const isDirectGuideGenerating = useCallback(
    (jobId: string) => pendingDirectGuides.has(jobId),
    [pendingDirectGuides],
  );

  // State for when guide generation is being prepared (before jobId is available)
  const [isPreparingGuide, setIsPreparingGuide] = useState(false);

  // Computed value for nav indicator - shows spinner if any SOP or direct guide is pending,
  // or if a guide is being prepared (before the edge function returns with a jobId)
  const hasAnyPending =
    pendingGuides.size > 0 || pendingDirectGuides.size > 0 || isPreparingGuide;

  return {
    pendingGuides,
    pendingDirectGuides,
    addPendingGuide,
    deletePendingGuide,
    updatePendingGuideJobId,
    addPendingDirectGuide,
    deletePendingDirectGuide,
    isGenerating,
    isDirectGuideGenerating,
    hasAnyPending,
    isPreparingGuide,
    setIsPreparingGuide,
    // Storage error handling
    lastStorageError,
    clearStorageError,
  };
}
